import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode } from "vue"
import _imports_0 from '@/assets.app/images/14335_checked.svg'


const _hoisted_1 = { class: "completion-popup" }
const _hoisted_2 = { class: "completion-popup__content" }
const _hoisted_3 = { class: "completion-popup__header" }
const _hoisted_4 = { class: "completion-popup__book" }
const _hoisted_5 = { class: "completion-popup__book-details" }
const _hoisted_6 = { class: "completion-popup__book-title" }
const _hoisted_7 = { class: "completion-popup__book-author" }
const _hoisted_8 = { class: "completion-popup__sections" }
const _hoisted_9 = { class: "completion-popup__takeaway-list" }
const _hoisted_10 = ["innerHTML"]
const _hoisted_11 = { class: "completion-popup__highlight-count" }
const _hoisted_12 = { class: "completion-popup__button-text" }
const _hoisted_13 = { class: "completion-popup__highlight-list" }
const _hoisted_14 = {
  key: 2,
  class: "completion-popup__missing-highlights"
}
const _hoisted_15 = { class: "completion-popup__exercise-count" }
const _hoisted_16 = { class: "completion-popup__button-text" }
const _hoisted_17 = { class: "completion-popup__exercise-list" }
const _hoisted_18 = ["disabled", "onClick"]
const _hoisted_19 = { class: "completion-popup__exercise-title" }
const _hoisted_20 = {
  key: 0,
  class: "completion-popup__exercise-status",
  src: _imports_0,
  alt: "Completed"
}
const _hoisted_21 = {
  key: 1,
  class: "completion-popup__exercise-status iconfont inext",
  "aria-hidden": "true"
}
const _hoisted_22 = { class: "completion-popup__actions" }

import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import { Mixpanel } from '@/analytics/mixpanel'
import type { CompletionFlowAction } from '@/analytics/mixpanel/mixpanel'
import type {
  CompletionExerciseItemData,
  CompletionHighlightItemData,
  CompletionPopupData,
  CompletionRecommendationData,
} from '@/models/completion'
import { emitter } from '@/services/mitt'

import AppButton from '@/app/components/button/AppButton.vue'
import BookCover from '@/app/components/book/card/BookCover.vue'
import CompletionAccordion from './CompletionAccordion.vue'
import CompletionHighlight from './CompletionHighlight.vue'
import CompletionMenu from './CompletionMenu.vue'
import CompletionRecommendations from './CompletionRecommendations.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'CompletionPopup',
  props: {
    data: {}
  },
  emits: ["dismiss"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const router = useRouter()
const menuOpen = ref(false)
const completedExerciseCount = computed(
  () => props.data.exercises.filter((exercise) => exercise.completed).length,
)

onMounted(() => {
  document.addEventListener('keydown', onKeydown)
  emitter.on('closeOverlayMenu', closeTopLayer)
  emitter.emit('overlayMenuOpened')
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
  emitter.off('closeOverlayMenu', closeTopLayer)
  emitter.emit('overlayMenuClosed')
})

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    closeTopLayer()
  }
}

function closeTopLayer(): void {
  if (menuOpen.value) {
    menuOpen.value = false
  } else {
    close()
  }
}

function dismiss(): void {
  menuOpen.value = false
  emit('dismiss')
}

function trackAction(
  action: CompletionFlowAction,
  recommendation?: CompletionRecommendationData,
  position?: number,
): void {
  Mixpanel.trackCompletionFlowAction(
    props.data.book.id,
    action,
    recommendation && position
      ? {
          guideId: recommendation.book.id,
          category: recommendation.book.tags[0],
          position,
        }
      : undefined,
  )
}

function close(): void {
  trackAction('close')
  dismiss()
}

function continueFlow(): void {
  trackAction('continue')
  dismiss()
}

function openHighlight(highlight: CompletionHighlightItemData): void {
  trackAction('open-highlight')
  dismiss()
  void router.push({
    name: 'book.page',
    params: {
      url_slug: props.data.book.url_slug,
      page_url_slug: highlight.content.url_slug,
    },
    query: { highlight: highlight.id },
  })
}

function addHighlight(): void {
  trackAction('add-highlight')
  dismiss()
  void router.push({
    name: 'book.page',
    params: {
      url_slug: props.data.book.url_slug,
      page_url_slug: '',
    },
  })
}

function exerciseTitle(title: string): string {
  return title.replace(/^Exercise: /, '')
}

function openExercise(exercise: CompletionExerciseItemData): void {
  trackAction('open-exercise')
  dismiss()
  void router.push({
    name: 'book.page',
    params: {
      url_slug: props.data.book.url_slug,
      page_url_slug: exercise.url_slug,
    },
  })
}

function openRecommendation(
  recommendation: CompletionRecommendationData,
  position: number,
): void {
  trackAction('open-recommendation', recommendation, position)
  dismiss()
  void router.push({
    name: 'book.preview',
    params: { url_slug: recommendation.book.url_slug },
  })
}

function trackRecommendationFavorite(
  recommendation: CompletionRecommendationData,
  position: number,
  action: 'favorite-recommendation' | 'unfavorite-recommendation',
): void {
  trackAction(action, recommendation, position)
}

function trackRecommendationImpression(
  recommendation: CompletionRecommendationData,
  position: number,
): void {
  Mixpanel.trackCompletionFlowRecommendationShown(
    props.data.book.id,
    recommendation,
    position,
  )
}

/**
 * Escape a takeaway and render its bold and italic Markdown.
 *
 * Takeaways are short paragraphs where bold and italic text are useful, but
 * other Markdown elements do not belong in this part of the popup. We handle
 * those two constructs directly so this component does not need a full
 * Markdown dependency or accept unsupported Markdown elements.
 */
function renderInlineMarkdown(text: string): string {
  // The stored takeaway text is LLM generated and not fully trusted, but the
  // result of this call has to be rendered with v-html because we are
  // manually adding HTML tags, so we use the browser to first escape any
  // prior HTML tags by creating a temporary element and then reading back
  // escaped text from innerHTML.
  const element = document.createElement('span')
  element.textContent = text
  return element.innerHTML
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("button", {
      class: "completion-popup__close",
      onClick: close
    }, [...(_cache[1] || (_cache[1] = [
      _createElementVNode("span", { class: "iconfont iclose-thin" }, null, -1)
    ]))]),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("header", _hoisted_3, [
        _cache[2] || (_cache[2] = _createElementVNode("span", {
          class: "completion-popup__celebration",
          "aria-hidden": "true"
        }, "🎉", -1)),
        _cache[3] || (_cache[3] = _createElementVNode("h1", { class: "completion-popup__title" }, "You finished the guide", -1)),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(BookCover, {
            class: "completion-popup__book-cover",
            doc: {
              title: __props.data.book.title,
              author: __props.data.book.author,
              cover_image: __props.data.book.cover_image ?? undefined,
            }
          }, null, 8, ["doc"]),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("h2", _hoisted_6, _toDisplayString(__props.data.book.title), 1),
            _createElementVNode("p", _hoisted_7, " By " + _toDisplayString(__props.data.book.author), 1)
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_8, [
        (__props.data.takeaways.length)
          ? (_openBlock(), _createBlock(CompletionAccordion, {
              key: 0,
              class: "completion-popup__takeaways",
              title: "💡 Key Takeaways",
              defaultOpen: ""
            }, {
              default: _withCtx(() => [
                _createElementVNode("ul", _hoisted_9, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.data.takeaways, (takeaway, index) => {
                    return (_openBlock(), _createElementBlock("li", {
                      key: index,
                      class: "completion-popup__takeaway",
                      innerHTML: renderInlineMarkdown(takeaway)
                    }, null, 8, _hoisted_10))
                  }), 128))
                ])
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (__props.data.highlights.count)
          ? (_openBlock(), _createBlock(CompletionAccordion, {
              key: 1,
              class: "completion-popup__highlights",
              title: "📌 Highlights",
              showDivider: false
            }, {
              badge: _withCtx(() => [
                _createElementVNode("span", _hoisted_11, [
                  _createElementVNode("span", _hoisted_12, _toDisplayString(__props.data.highlights.count), 1)
                ])
              ]),
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_13, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.data.highlights.items, (highlight) => {
                    return (_openBlock(), _createBlock(CompletionHighlight, {
                      key: highlight.id,
                      quote: highlight.quote,
                      onOpen: ($event: any) => (openHighlight(highlight))
                    }, null, 8, ["quote", "onOpen"]))
                  }), 128))
                ])
              ]),
              _: 1
            }))
          : (_openBlock(), _createElementBlock("section", _hoisted_14, [
              _createElementVNode("div", { class: "completion-popup__missing-highlights-header" }, [
                _cache[5] || (_cache[5] = _createElementVNode("h2", { class: "completion-popup__missing-highlights-title" }, " 📌 Highlights ", -1)),
                _createElementVNode("button", {
                  class: "completion-popup__add-highlight",
                  type: "button",
                  onClick: addHighlight
                }, [...(_cache[4] || (_cache[4] = [
                  _createElementVNode("span", { class: "completion-popup__button-text" }, "Add highlight", -1)
                ]))])
              ]),
              _cache[6] || (_cache[6] = _createElementVNode("p", { class: "completion-popup__highlight-nudge" }, " Highlight key points to make them stick ", -1))
            ])),
        (__props.data.exercises.length)
          ? (_openBlock(), _createBlock(CompletionAccordion, {
              key: 3,
              class: "completion-popup__exercises",
              title: "✏️ Exercises"
            }, {
              badge: _withCtx(() => [
                _createElementVNode("span", _hoisted_15, [
                  _createElementVNode("span", _hoisted_16, _toDisplayString(completedExerciseCount.value) + "/" + _toDisplayString(__props.data.exercises.length), 1)
                ])
              ]),
              default: _withCtx(() => [
                _createElementVNode("ul", _hoisted_17, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.data.exercises, (exercise) => {
                    return (_openBlock(), _createElementBlock("li", {
                      key: exercise.url_slug,
                      class: "completion-popup__exercise-item"
                    }, [
                      _createElementVNode("button", {
                        class: _normalizeClass(["completion-popup__exercise", {
                  'completion-popup__exercise--completed': exercise.completed,
                }]),
                        disabled: exercise.completed,
                        onClick: ($event: any) => (openExercise(exercise))
                      }, [
                        _createElementVNode("span", _hoisted_19, [
                          _createElementVNode("span", null, _toDisplayString(exerciseTitle(exercise.title)), 1)
                        ]),
                        (exercise.completed)
                          ? (_openBlock(), _createElementBlock("img", _hoisted_20))
                          : (_openBlock(), _createElementBlock("span", _hoisted_21))
                      ], 10, _hoisted_18)
                    ]))
                  }), 128))
                ])
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      (__props.data.recommendations.length)
        ? (_openBlock(), _createBlock(CompletionRecommendations, {
            key: 0,
            recommendations: __props.data.recommendations,
            onSelect: openRecommendation,
            onFavorite: trackRecommendationFavorite,
            onImpression: trackRecommendationImpression
          }, null, 8, ["recommendations"]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_22, [
      _createVNode(AppButton, {
        class: "completion-popup__continue",
        big: "",
        onClick: continueFlow
      }, {
        default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
          _createTextVNode(" Continue ", -1)
        ]))]),
        _: 1
      }),
      _createVNode(CompletionMenu, {
        modelValue: menuOpen.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((menuOpen).value = $event)),
        data: __props.data,
        onDismiss: dismiss
      }, null, 8, ["modelValue", "data"])
    ])
  ]))
}
}

})